import { useCallback, useEffect, useState } from 'react';

import { errorKey, type AuthApi, type Round } from '../auth/api.ts';
import { useI18n } from '../i18n/I18nProvider';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import './history.css';

const PAGE = 20;

/**
 * 投注历史。
 *
 * ## 往下续，不翻页
 *
 * 下级名单那种「上一页 / 下一页」在这里不对：局是**一直在新增**的，
 * 而按页翻的时候新的一局插在最前面，第 2 页就会把第 1 页的最后一条再显示
 * 一遍 —— "同一笔下注出现两次"在一个赌钱的产品里是客服电话的第一句话。
 *
 * 游标 + 往下续就没有这个问题：每一页都是从上一条**之后**接着取。
 *
 * ## 不显示盘面
 *
 * 玩家要看的是"什么时候、哪个游戏、押了多少、中了多少"。要验一局对不对，
 * 走后台的重放接口 —— 客户端自己算出来的结论本来就不作数。
 *
 * ## 免费局一样要列出来
 *
 * 它的下注是 0。藏起来的话，玩家会看到余额涨了却找不到对应的一局，
 * 然后就来问是不是记错账了。标一个角标，说明它是免费局。
 */
export function HistorySheet(
  { open, api, token, money, onClose }:
  {
    open: boolean; api: AuthApi; token: string;
    money(n: number): string; onClose(): void;
  },
) {
  const { t, i18n } = useI18n();
  const [rounds, setRounds] = useState<Round[]>([]);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // 装过没有。**空列表和"还没装"要分得开** —— 不分的话，一打开就会
  // 先闪一下"还没有任何一局"，然后数据才到。
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async (cursor: string | null) => {
    setBusy(true);
    try {
      const p = await api.rounds(token, cursor, PAGE);
      // 续在后面，不是替换 —— 替换的话"加载更多"会变成"跳到下一页"。
      setRounds((old) => (cursor ? [...old, ...p.rounds] : p.rounds));
      setMore(p.more);
      setErr('');
    } catch (e) {
      setErr(t(errorKey(e)));
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  }, [api, token, t]);

  // 每次打开都从头拉一次。缓存上一次的结果没有意义 ——
  // 两次打开之间他很可能刚打了几局，而那几局正是他要看的。
  useEffect(() => {
    if (!open) return;
    setRounds([]); setMore(false); setLoaded(false);
    void load(null);
  }, [open, load]);

  return (
    <Sheet open={open} onClose={onClose} title={t('history.title')}>
      {err && <p className="hist__err">{err}</p>}

      {loaded && rounds.length === 0 && !err && (
        <p className="hist__empty">{t('history.empty')}</p>
      )}

      <ul className="hist__list">
        {rounds.map((r) => {
          const bet = Number(r.bet);
          const win = Number(r.win);
          return (
            <li key={r.id} className="hist__row">
              <span className="hist__who">
                <b>
                  {r.gameName}
                  {r.status && <span className="hist__tag">{t(`history.status.${r.status}`)}</span>}
                  {r.mode === 'free' && (
                    <span className="hist__tag">{t('history.free')}</span>
                  )}
                  {r.mode === 'reward' && (
                    <span className="hist__tag">{t('history.reward')}</span>
                  )}
                </b>
                {r.at && <time>{i18n.dateTime(Date.parse(r.at))}</time>}
              </span>
              <span className="hist__meta">
                {/* 下注写成负数：这一列上下扫过去就是"出去多少、回来多少"，
                    不用再去读旁边那个字。免费局是 0，显示成 —— */}
                <span className="hist__bet num">
                  {bet > 0 ? `-${money(bet)}` : '—'}
                </span>
                <span className={win > 0 ? 'hist__win num is-win' : 'hist__win num'}>
                  {win > 0 ? `+${money(win)}` : money(0)}
                </span>
                {Number(r.adjustment ?? 0) !== 0 && (
                  <span className="num">{t('history.adjustment')} {Number(r.adjustment) > 0 ? '+' : '−'}{money(Math.abs(Number(r.adjustment)))}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {more && (
        <Button variant="ghost" disabled={busy}
                onClick={() => load(rounds[rounds.length - 1]?.cursor ?? null)}>
          {t(busy ? 'action.working' : 'history.more')}
        </Button>
      )}
    </Sheet>
  );
}
